import {
  CheckCircleRounded as SuccessIcon,
  ErrorRounded as FailedIcon,
  ExpandMoreRounded as ExpandIcon,
  HourglassTopRounded as InProgressIcon,
  RocketLaunchRounded as RocketLaunchIcon,
  ScheduleRounded as PendingIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  Paper,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { humanizeKey, toSentenceCase } from "common/strings";
import Component from "components/Component";
import Options from "components/common/Options";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import * as adminService from "services/adminService";

const PENDING_STATUS = "Pending";
const PENDING_TIMEOUT_MS = 2 * 60 * 1000;

const isInProgress = (execution) => execution?.status === "InProgress";

const STATUS_COLOURS = {
  [PENDING_STATUS]: "info",
  Succeeded: "success",
  InProgress: "warning",
  Failed: "error",
  Stopped: "default",
  Unknown: "default",
};

const STATUS_ICONS = {
  [PENDING_STATUS]: <PendingIcon />,
  Succeeded: <SuccessIcon />,
  InProgress: <InProgressIcon />,
  Failed: <FailedIcon />,
};

const PipelineStatusBadge = ({ status }) => (
  <Chip
    color={STATUS_COLOURS[status] ?? "default"}
    icon={STATUS_ICONS[status]}
    label={humanizeKey(status ?? "Unknown")}
    size={"small"}
  />
);

const InfoLine = ({ label, children }) => (
  <Typography variant={"body2"} color={"text.secondary"}>
    <b>{`${label}: `}</b>
    {children}
  </Typography>
);

const CommitInfo = ({ execution }) => {
  if (!execution) return null;
  const sha = execution.commit_sha ?? "—";
  const message = execution.commit_message ?? "—";
  const startedAt = execution.started_at
    ? new Date(execution.started_at).toLocaleString()
    : "—";
  return (
    <Stack direction={"column"} spacing={0.5}>
      <InfoLine label={"Commit"}>{`${sha} — ${message}`}</InfoLine>
      <InfoLine label={"Author"}>{execution.commit_author ?? "—"}</InfoLine>
      <InfoLine label={"Started"}>{startedAt}</InfoLine>
    </Stack>
  );
};

const DEPLOY_OPTIONS = [
  {
    key: "retry",
    label: "Retry Latest",
    serviceNames: undefined,
    dialogTitle: "Retry latest deployment?",
    dialogBody:
      "This will re-run the pipeline on the current commit, deploying any changed services",
  },
  {
    key: "all",
    label: "Deploy All",
    serviceNames: ["*"],
    dialogTitle: "Deploy all services?",
    dialogBody:
      "This will re-deploy all services, regardless of whether they have changed",
  },
  {
    key: "custom",
    label: "Custom…",
    serviceNames: null,
  },
];

const CustomDeployModal = ({ open, onClose, onConfirm }) => {
  const {
    data,
    isFetching,
  } = adminService.useGetDeployableServicesQuery(undefined, { skip: !open });
  const services = data?.services ?? [];
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (open) setSelected([]);
  }, [open]);

  const toggle = (name) =>
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>{"Deploy custom services"}</DialogTitle>
      <DialogContent>
        {isFetching ? (
          <CircularProgress size={24} />
        ) : (
          <Paper variant={"outlined"} elevation={0} sx={{ p: "1rem" }}>
            <Options
              items={services.map((name) => ({
                id: name,
                label: toSentenceCase(name),
                selected: selected.includes(name),
              }))}
              onClick={({ id }) => toggle(id)}
              onToggleAll={(allSelected) =>
                setSelected(allSelected ? [] : services)
              }
            />
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{"Cancel"}</Button>
        <Button
          variant={"contained"}
          color={"error"}
          disableElevation
          disabled={selected.length === 0}
          onClick={() => onConfirm(selected)}
          sx={{ minWidth: "7.5rem" }}
        >
          {`Deploy ${selected.length > 0 ? `(${selected.length})` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TriggerDeployButton = ({ onTrigger, isTriggering }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const anchorRef = useRef(null);
  const selectedOption = DEPLOY_OPTIONS[selectedIndex];

  const handleDeployClick = () => {
    if (selectedOption.key === "custom") {
      setCustomOpen(true);
    } else {
      setConfirmOpen(true);
    }
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    await onTrigger(selectedOption.serviceNames);
  };

  const handleCustomConfirm = async (serviceNames) => {
    setCustomOpen(false);
    await onTrigger(serviceNames);
  };

  return (
    <>
      <ButtonGroup
        variant={"contained"}
        color={"primary"}
        disableElevation
        disabled={isTriggering}
        ref={anchorRef}
      >
        <Button startIcon={<RocketLaunchIcon />} onClick={handleDeployClick}>
          {selectedOption.label}
        </Button>
        <Button size={"small"} onClick={() => setMenuOpen((prev) => !prev)}>
          <ExpandIcon />
        </Button>
      </ButtonGroup>
      <Menu
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {DEPLOY_OPTIONS.map((option, index) => (
          <MenuItem
            key={option.key}
            selected={index === selectedIndex}
            onClick={() => {
              setSelectedIndex(index);
              setMenuOpen(false);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{selectedOption.dialogTitle}</DialogTitle>
        <DialogContent>
          <Typography>{selectedOption.dialogBody}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{"Cancel"}</Button>
          <Button
            variant={"contained"}
            color={"error"}
            disableElevation
            onClick={handleConfirm}
          >
            {"Deploy"}
          </Button>
        </DialogActions>
      </Dialog>
      <CustomDeployModal
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        onConfirm={handleCustomConfirm}
      />
    </>
  );
};

const LogViewer = ({ executionId, isInProgress }) => {
  const theme = useTheme();
  const [logLines, setLogLines] = useState([]);
  const [nextToken, setNextToken] = useState(undefined);
  const [
    fetchLogs,
  ] = adminService.useLazyGetDeploymentPipelineExecutionLogsQuery();
  const bottomRef = useRef(null);

  const fetchMore = useCallback(async () => {
    const result = await fetchLogs({ executionId, nextToken });
    if (result.data?.events?.length) {
      setLogLines((prev) => [...prev, ...result.data.events]);
      setNextToken(result.data.next_token);
    }
  }, [executionId, fetchLogs, nextToken]);

  useEffect(() => {
    fetchMore();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isInProgress) return;
    const interval = setInterval(fetchMore, 4000);
    return () => clearInterval(interval);
  }, [isInProgress, fetchMore]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logLines]);

  return (
    <Box
      sx={{
        bgcolor: "#1a1a1a",
        color: theme.brand.gray.gainsboro,
        fontFamily: "monospace",
        fontSize: "0.75rem",
        p: 1.5,
        borderRadius: 1,
        maxHeight: 400,
        overflowY: "auto",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
      }}
    >
      {logLines.length === 0 ? (
        <Typography color={"inherit"} fontSize={"inherit"}>
          {"Loading logs…"}
        </Typography>
      ) : (
        logLines.map((event, i) => <div key={i}>{event.message}</div>)
      )}
      <div ref={bottomRef} />
    </Box>
  );
};

const ExecutionRow = ({ execution }) => {
  const [logsOpen, setLogsOpen] = useState(false);
  const inProgress = isInProgress(execution);

  useEffect(() => {
    if (inProgress) setLogsOpen(true);
  }, [inProgress]);

  return (
    <Card variant={"outlined"}>
      <CardContent>
        <Stack direction={"column"} spacing={1}>
          <Stack direction={"row"} spacing={2} alignItems={"center"}>
            <PipelineStatusBadge status={execution.status} />
            <Typography variant={"body2"} color={"text.secondary"}>
              {execution.execution_id}
            </Typography>
            {inProgress && <CircularProgress size={14} />}
          </Stack>
          <CommitInfo execution={execution} />
          <Accordion
            expanded={logsOpen}
            onChange={(_, expanded) => setLogsOpen(expanded)}
            disableGutters
            elevation={0}
            sx={{ border: "none", "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ px: 0 }}>
              <Typography variant={"caption"}>{"Logs"}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              {logsOpen && (
                <LogViewer
                  executionId={execution.execution_id}
                  isInProgress={inProgress}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </Stack>
      </CardContent>
    </Card>
  );
};

const describeServices = (serviceNames) => {
  if (!serviceNames) return "Changed services";
  if (serviceNames.includes("*")) return "All services";
  return serviceNames.map(toSentenceCase).join(", ");
};

const PendingExecutionRow = ({ pending }) => (
  <Card variant={"outlined"}>
    <CardContent>
      <Stack direction={"column"} spacing={1}>
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <PipelineStatusBadge status={PENDING_STATUS} />
          <Typography variant={"body2"} color={"text.secondary"}>
            {pending.executionId ?? "Starting…"}
          </Typography>
          <CircularProgress size={14} />
        </Stack>
        <Stack direction={"column"} spacing={0.5}>
          <InfoLine label={"Services"}>
            {describeServices(pending.serviceNames)}
          </InfoLine>
          <InfoLine label={"Triggered"}>
            {new Date(pending.triggeredAt).toLocaleString()}
          </InfoLine>
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

const StatusSummaryCard = ({ status, pending, onTrigger, isTriggering }) => (
  <Card variant={"outlined"}>
    <CardHeader
      title={
        <Typography variant={"h6"}>{"Current Pipeline Status"}</Typography>
      }
      action={
        <TriggerDeployButton
          onTrigger={onTrigger}
          isTriggering={isTriggering}
        />
      }
    />
    <CardContent>
      <Stack direction={"column"} spacing={2}>
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <PipelineStatusBadge
            status={pending ? PENDING_STATUS : status?.status}
          />
          {!pending && status?.current_stage && (
            <Typography variant={"body2"} color={"text.secondary"}>
              {`Stage: ${status.current_stage}`}
            </Typography>
          )}
        </Stack>
        {pending ? (
          <InfoLine label={"Services"}>
            {describeServices(pending.serviceNames)}
          </InfoLine>
        ) : (
          <CommitInfo execution={status?.latest_execution} />
        )}
      </Stack>
    </CardContent>
  </Card>
);

const PipelineStatus = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [pending, setPending] = useState(null);

  // State, because the status query's own options depend on its last result.
  const [isStatusInProgress, setIsStatusInProgress] = useState(false);
  const isActive = Boolean(pending) || isStatusInProgress;

  const {
    data: pipelineStatus,
    isFetching: isStatusFetching,
    refetch: refetchStatus,
  } = adminService.useGetDeploymentPipelineStatusQuery(undefined, {
    pollingInterval: isActive ? 5000 : 30000,
  });

  const {
    data: executions,
    isFetching: isExecutionsFetching,
    refetch: refetchExecutions,
  } = adminService.useListDeploymentPipelineExecutionsQuery(undefined, {
    pollingInterval: isActive ? 10000 : 60000,
  });

  useEffect(() => {
    setIsStatusInProgress(isInProgress(pipelineStatus));
  }, [pipelineStatus]);

  const pendingId = pending?.executionId;
  useEffect(() => {
    if (!pendingId) return;
    const isListed =
      pipelineStatus?.latest_execution?.execution_id === pendingId ||
      executions?.executions?.some((e) => e.execution_id === pendingId);
    if (isListed) setPending(null);
  }, [pendingId, executions, pipelineStatus]);

  // Keyed on triggeredAt so filling in executionId doesn't restart the timer.
  const pendingTriggeredAt = pending?.triggeredAt;
  useEffect(() => {
    if (!pendingTriggeredAt) return;
    const timer = setTimeout(() => {
      setPending(null);
      enqueueSnackbar(
        "Deployment still not listed by CodePipeline, try refreshing or check the pipeline directly",
        { variant: "warning" }
      );
    }, PENDING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [pendingTriggeredAt, enqueueSnackbar]);

  const [triggerDeploy] = adminService.useTriggerDeploymentPipelineMutation();

  const handleTrigger = async (serviceNames) => {
    setPending({ serviceNames, triggeredAt: Date.now() });
    try {
      const result = await triggerDeploy({ serviceNames }).unwrap();
      setPending(
        (prev) => prev && { ...prev, executionId: result?.execution_id }
      );
      refetchStatus();
      refetchExecutions();
    } catch {
      setPending(null);
      enqueueSnackbar("Failed to trigger deployment", { variant: "error" });
    }
  };

  const listedExecutions = executions?.executions ?? [];

  return (
    <Component
      title={"Deployment Pipeline"}
      isFetching={isStatusFetching || isExecutionsFetching}
      fadeOnFetch={false}
    >
      <Stack direction={"column"} spacing={3}>
        <StatusSummaryCard
          status={pipelineStatus}
          pending={pending}
          onTrigger={handleTrigger}
          isTriggering={Boolean(pending)}
        />
        <Typography variant={"h6"} pt={4}>
          {"Execution History"}
        </Typography>
        <Stack direction={"column"} spacing={2}>
          {pending && <PendingExecutionRow pending={pending} />}
          {listedExecutions.map((execution) => (
            <ExecutionRow key={execution.execution_id} execution={execution} />
          ))}
          {!pending && !listedExecutions.length && !isExecutionsFetching && (
            <Typography color={"text.secondary"}>
              {"No executions found."}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Component>
  );
};

export default PipelineStatus;
